import Head from 'next/head';
import Image from 'next/image';
import {useRouter} from 'next/router';
import {parseCookies} from 'nookies';
import {type FC, useEffect, useRef, useState} from 'react';
import {MdArrowBack} from 'react-icons/md';
import {TITLE_SUFFIX} from '../../../config/constants';
import {GoogleAdBannerName} from '../../../config/constants/googleAdBanner';
import {GSSKT_HOST} from '../../../config/constants/hosts';
import {BOX_ANSWERS, DISCLAIMER} from '../../../config/constants/routes';
import {isReleasedRedesignRecruitment} from '../../../config/unReleasePage';
import {RECRUITMENT_SCHEDULE_WARNING_STATUS} from '../../../core/entities/recruitment/constants';
import type {RecruitmentDetailResponse} from '../../../core/entities/recruitment/schema';
import {useGetCompanyInfo} from '../../../core/repositories/companiesRepository';
import {validateCookie} from '../../../core/usecases/auth/checkOauthTokenExistence';
import {GoogleAdBanner} from '../../../shared/components/Banner/GoogleAdBanner';
import {BreadcrumbsComponent} from '../../../shared/components/BreadcrumbsComponent';
import {CommonLink} from '../../../shared/components/CommonLink';
import {createJobPostingStructuredData} from '../../../shared/services/createJobPostingStructuredData';
import {useGoogleAdBanner} from '../../../shared/services/useGoogleAdBanner';
import {useMediaQuery} from '../../../shared/services/useMediaQuery';
import {getScheduleWarningStatus} from '../usecases/getScheduleWarningStatus';
import {useCompanyDataDTO} from '../usecases/useCompanyDataDTO';
import {useOtherRecruitmentList} from '../usecases/useOtherRecruitmentList';
import {Appendixes} from './Container/Presenter/Appendixes';
import {BottomCompanyInfo} from './Container/Presenter/BottomCompanyInfo';
import {CompanyContentList} from './Container/Presenter/CompanyContentList';
import {CompanyContentTab} from './Container/Presenter/CompanyContentListTab';
import {EntryButton} from './Container/Presenter/EntryButton';
import {RecruitmentHeaderPC} from './Container/Presenter/RecruitmentHeaderPC';
import {RecruitmentHeaderSP} from './Container/Presenter/RecruitmentHeaderSP';
import {RecruitmentLeftColumn} from './Container/Presenter/RecruitmentLeftColumn';
import {RecruitmentTitle} from './Container/Presenter/RecruitmentTitle';
import {ResourceContentItem} from './Container/Presenter/ResourceContentItem';
import {ScheduleList} from './Container/Presenter/ScheduleList';
import {ScheduleWarning} from './Container/Presenter/ScheduleWarning';
import {TopCompanyInfo} from './Container/Presenter/TopCompanyInfo';
import {TransitionEntryButton} from './Container/Presenter/TransitionEntryButton';
import {UpdatedBy} from './Container/Presenter/UpdatedAt';
import {RelatedContentList} from './Container/RelatedContentList/RelatedContentList';
import {SpecialPostList} from './Container/SpecialPostList';
import {WebSeminarList} from './Container/WebSeminarList';
import {OtherRecruitmentList} from './RecruitmentList/OtherRecruitmentList';
import {RelatedCompanyRecruitmentList} from './RecruitmentList/RelatedCompanyRecruitmentList';

type Props = {
  recruitmentDetailId: number;
  recruitmentDetail: RecruitmentDetailResponse;
  recruitmentItemId: number;
};

export const ViewPage: FC<Props> = ({
  recruitmentDetailId,
  recruitmentDetail,
  recruitmentItemId,
}) => {
  const cookies = parseCookies();
  const {isValid} = validateCookie(cookies);

  const router = useRouter();
  const currentUrl = `${GSSKT_HOST}${decodeURI(router.asPath)}`;
  const {isPC} = useMediaQuery();

  const googleAdBannerNames = [GoogleAdBannerName.D_FEED_LEFT_SMALL];
  useGoogleAdBanner(googleAdBannerNames);

  const pageTitle = `${recruitmentDetail.title}${recruitmentDetail.typeV2.key === 'intern' ? '｜インターンの募集情報詳細' : ''}${TITLE_SUFFIX}`;
  const description = recruitmentDetail.resources[0]?.content.slice(0, 79);

  const {
    companyId,
    companyName,
    companyIndustry,
    companyLogoImageUrl,
    hasReport,
  } = useCompanyDataDTO({recruitmentDetail});

  const scheduleWarningStatus = getScheduleWarningStatus(
    recruitmentDetail.scheduleWarning,
    recruitmentDetail.displayEndAt,
  );
  const hasScheduleWaring =
    scheduleWarningStatus !== RECRUITMENT_SCHEDULE_WARNING_STATUS.OK;

  const otherRecruitmentListRef = useRef<HTMLDivElement>(null);
  const scrollToOtherRecruitmentList = () => {
    otherRecruitmentListRef.current?.scrollIntoView({
      behavior: 'smooth',
    });
  };

  const {
    otherRecruitmentList,
    isLoadingOtherRecruitmentList,
    hasOtherRecruitmentList,
  } = useOtherRecruitmentList({
    companyId,
    recruitmentDetailId,
  });

  const {data: companyInfo, isLoading} = useGetCompanyInfo({companyId});

  // スクロールでエントリーボタン表示制御
  const [showEntryButton, setShowEntryButton] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      // 例: 300px以上スクロールしたら表示
      setShowEntryButton(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const crumbs = [
    {label: 'Home', href: '/'},
    {label: '募集を探す', href: '/recruiting_info'},
    {label: recruitmentDetail.title, href: currentUrl},
  ];

  return (
    <>
      <Head>
        <title key="title">{pageTitle}</title>
        <link rel="canonical" href={currentUrl} key="canonical" />
        <meta key="og:title" property="og:title" content={pageTitle} />
        <meta key="description" property="description" content={description} />
        <meta
          key="og:description"
          property="og:description"
          content={description}
        />
        <meta key="og:url" property="og:url" content={currentUrl} />
        <meta
          key="og:image"
          property="og:image"
          content={companyLogoImageUrl}
        />
        <meta key="twitter:title" name="twitter:title" content={pageTitle} />
        <meta
          key="twitter:description"
          name="twitter:description"
          content={description}
        />
        <meta
          key="twitter:image"
          name="twitter:image"
          content={companyLogoImageUrl}
        />
        <script type="application/ld+json">
          {JSON.stringify(
            createJobPostingStructuredData(
              recruitmentDetail,
              companyName,
              companyId,
              currentUrl,
              companyLogoImageUrl,
            ),
          )}
        </script>
      </Head>
      {isReleasedRedesignRecruitment() ? (
        <div className="w-full pc:w-auto">
          <div className="my-8 hidden pc:block">
            <BreadcrumbsComponent crumbs={crumbs} />
          </div>
          <div className="flex w-full justify-center gap-16 px-8 pc:px-0">
            <div className="sticky top-[100px] h-fit hidden pc:block">
              <RecruitmentLeftColumn
                companyId={companyId}
                companyName={companyName}
                companyIndustry={companyIndustry}
                companyLogoImageUrl={companyLogoImageUrl}
                companyFollowerCount={
                  companyInfo?.ok ? companyInfo.value.followerCount : 0
                }
                companyIsFollowed={
                  companyInfo?.ok ? companyInfo.value.followed : false
                }
                isLoading={isLoading}
                hasReport={hasReport}
                shareTitle={pageTitle}
                shareUrl={currentUrl}
              />
              <GoogleAdBanner
                googleAdBannerName={GoogleAdBannerName.D_FEED_LEFT_SMALL}
              />
            </div>
            <div className="flex flex-col pc:w-[814px] w-full gap-8">
              <div className="hidden pc:block">
                <RecruitmentHeaderPC
                  title={recruitmentDetail.title}
                  recruitmentType={recruitmentDetail.typeV2}
                  targetYear={recruitmentDetail.targetYear}
                  groupedJobTypes={recruitmentDetail.groupedJobTypes}
                  tags={recruitmentDetail.tags}
                  entryURL={hasScheduleWaring ? '' : recruitmentDetail.entryURL}
                  schedules={recruitmentDetail.schedules}
                  modifiedAt={recruitmentDetail.modifiedAt}
                />
              </div>
              <div className="pc:hidden flex flex-col gap-8">
                <div className="flex bg-white-1 rounded-16 shadow-card p-16">
                  <CommonLink href="/recruiting_info">
                    <MdArrowBack
                      className="bg-background rounded-16"
                      size={24}
                    />
                  </CommonLink>
                  <p className="ml-8 font-bold text-lg">募集を探す</p>
                </div>
                <RecruitmentHeaderSP
                  company={{
                    id: companyId,
                    name: companyName,
                    industry: companyIndustry,
                    logoURL: companyLogoImageUrl,
                    isFollowed: companyInfo?.ok
                      ? companyInfo.value.followed
                      : false,
                    followerCount: companyInfo?.ok
                      ? companyInfo.value.followerCount
                      : 0,
                    isLoading: isLoading,
                  }}
                  hasReport={hasReport}
                  title={recruitmentDetail.title}
                  recruitmentType={recruitmentDetail.typeV2}
                  targetYear={recruitmentDetail.targetYear}
                  groupedJobTypes={recruitmentDetail.groupedJobTypes}
                  tags={recruitmentDetail.tags}
                  entryURL={hasScheduleWaring ? '' : recruitmentDetail.entryURL}
                  schedules={recruitmentDetail.schedules}
                  modifiedAt={recruitmentDetail.modifiedAt}
                  shareTitle={pageTitle}
                  shareUrl={currentUrl}
                />
              </div>
              <ScheduleWarning
                companyName={companyName}
                scheduleWarning={recruitmentDetail.scheduleWarning}
                hasOtherRecruitmentList={hasOtherRecruitmentList}
                displayEndAt={recruitmentDetail.displayEndAt}
                scrollToOtherRecruitmentList={scrollToOtherRecruitmentList}
              />

              {recruitmentDetail.appendixes && (
                <Appendixes appendixes={recruitmentDetail.appendixes} />
              )}

              {recruitmentDetail.resources.map((resource) => {
                return (
                  <ResourceContentItem
                    key={resource.resourceID}
                    resource={resource}
                    isHydrated={true}
                  />
                );
              })}

              <div className="mt-16">
                <p className="text-sm text-black leading">
                  弊サイトは情報の正確性に万全を期しておりますが、対象企業による募集情報の急な変更などにより、締切日等について最新の情報を掲載できない場合がございます。
                  <br />
                  大変お手数ですがご志望の企業につきましては、各社の求人情報ページにて最新の情報をご参照いただくようお願い致します。
                  <CommonLink
                    href={DISCLAIMER}
                    className="text-sm text-link underline pc:hover:opacity-80"
                  >
                    こちらの免責事項
                  </CommonLink>
                  もご確認ください。
                </p>
              </div>
              <WebSeminarList companyId={companyId} />
              <SpecialPostList
                companyId={companyId}
                companyName={companyName}
              />
              <OtherRecruitmentList
                companyName={companyName}
                scheduleWarning={recruitmentDetail.scheduleWarning}
                otherRecruitmentList={otherRecruitmentList}
                isLoadingOtherRecruitmentList={isLoadingOtherRecruitmentList}
                hasOtherRecruitmentList={hasOtherRecruitmentList}
                otherRecruitmentListRef={otherRecruitmentListRef}
              />
              <RelatedCompanyRecruitmentList
                companyId={companyId}
                companyName={companyName}
              />
            </div>
          </div>
          {!hasScheduleWaring && recruitmentDetail.entryURL && (
            <TransitionEntryButton
              entryURL={recruitmentDetail.entryURL}
              mounted={showEntryButton}
              duration={500}
              className="fixed bottom-0 left-0 w-full py-8 bg-white"
              buttonClassName="px-36 pc:px-64 pc:py-16 text-sm bg-gradient-to-r from-error to-sub font-bold rounded-[8px] hover:opacity-80"
              transition="slide-up"
            />
          )}
        </div>
      ) : (
        <div className="w-full">
          <TopCompanyInfo
            companyId={companyId}
            companyName={companyName}
            companyIndustry={companyIndustry}
          />
          <div className="mt-16 flex">
            {isPC && (
              <div className="w-[200px]">
                <CompanyContentList
                  companyId={companyId}
                  title={pageTitle}
                  currentUrl={currentUrl}
                  entryURL={hasScheduleWaring ? '' : recruitmentDetail.entryURL}
                  hasReport={hasReport}
                />
                <GoogleAdBanner
                  googleAdBannerName={GoogleAdBannerName.D_FEED_LEFT_SMALL}
                />
              </div>
            )}
            {/* 中央 */}
            <div className="w-full pc:w-[664px] pc:ml-12">
              {!isPC && (
                <CompanyContentTab
                  companyId={companyId}
                  hasReport={hasReport}
                />
              )}
              <RecruitmentTitle
                title={recruitmentDetail.title}
                recruitmentType={recruitmentDetail.typeV2}
                targetYear={recruitmentDetail.targetYear}
                groupedJobTypes={recruitmentDetail.groupedJobTypes}
                tags={recruitmentDetail.tags}
              />
              <div className="w-full bg-white p-16 pc:p-24 my-16">
                {recruitmentDetail.schedules.length > 0 && (
                  <ScheduleList schedules={recruitmentDetail.schedules} />
                )}
                <ScheduleWarning
                  companyName={companyName}
                  scheduleWarning={recruitmentDetail.scheduleWarning}
                  hasOtherRecruitmentList={hasOtherRecruitmentList}
                  displayEndAt={recruitmentDetail.displayEndAt}
                  scrollToOtherRecruitmentList={scrollToOtherRecruitmentList}
                />
                {isPC && (
                  <Appendixes appendixes={recruitmentDetail.appendixes} />
                )}
                {!isPC && !hasScheduleWaring && (
                  <EntryButton entryURL={recruitmentDetail.entryURL} />
                )}
                {!isPC && (
                  <Appendixes appendixes={recruitmentDetail.appendixes} />
                )}
                <div className="mt-16">
                  {recruitmentDetail.resources.map((resource) => {
                    return (
                      <ResourceContentItem
                        key={resource.resourceID}
                        resource={resource}
                        isHydrated={true}
                      />
                    );
                  })}
                </div>
                {!hasScheduleWaring && (
                  <EntryButton entryURL={recruitmentDetail.entryURL} />
                )}
                <WebSeminarList companyId={companyId} />
                <SpecialPostList
                  companyId={companyId}
                  companyName={companyName}
                />
                <UpdatedBy updatedAt={recruitmentDetail.modifiedAt} />
                <BottomCompanyInfo
                  companyId={companyId}
                  companyName={companyName}
                  logoImageUrl={companyLogoImageUrl}
                  industry={companyIndustry}
                  isPC={isPC}
                />
                <div className="mt-16">
                  <p className="text-sm text-black leading">
                    弊サイトは情報の正確性に万全を期しておりますが、対象企業による募集情報の急な変更などにより、締切日等について最新の情報を掲載できない場合がございます。
                    <br />
                    大変お手数ですがご志望の企業につきましては、各社の求人情報ページにて最新の情報をご参照いただくようお願い致します。
                    <CommonLink
                      href={DISCLAIMER}
                      className="text-sm text-link underline pc:hover:opacity-80"
                    >
                      こちらの免責事項
                    </CommonLink>
                    もご確認ください。
                  </p>
                </div>
                {!isPC && (
                  <div className="mt-16">
                    <CommonLink
                      href={BOX_ANSWERS}
                      className="w-full h-[400px] relative block"
                    >
                      <Image
                        src={'/next-image/box/banner/square_banner_for_sp.jpg'}
                        alt={'外資就活相談室'}
                        className="object-cover"
                        fill={true}
                      />
                    </CommonLink>
                  </div>
                )}
              </div>
              <OtherRecruitmentList
                companyName={companyName}
                scheduleWarning={recruitmentDetail.scheduleWarning}
                otherRecruitmentList={otherRecruitmentList}
                isLoadingOtherRecruitmentList={isLoadingOtherRecruitmentList}
                hasOtherRecruitmentList={hasOtherRecruitmentList}
                otherRecruitmentListRef={otherRecruitmentListRef}
              />
              <RelatedCompanyRecruitmentList
                companyId={companyId}
                companyName={companyName}
              />
              {isPC && isValid && (
                <RelatedContentList recruitmentItemId={recruitmentItemId} />
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
